import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Mail, Phone, Building2, Calendar, Search, Download } from "lucide-react";

export default function PartnersAdmin() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/partners/list")
      .then((r) => setItems(r.data))
      .catch(() => { toast.error("Admin access required"); setItems([]); });
  }, []);

  if (items === null) return <div className="p-16 text-center text-slate-500">Loading…</div>;

  const filtered = items.filter((p) => {
    const s = q.toLowerCase().trim();
    if (!s) return true;
    return [p.name, p.email, p.organization, p.sector].some((v) => (v || "").toLowerCase().includes(s));
  });

  const exportCsv = () => {
    const rows = [
      ["Name", "Email", "Phone", "Organization", "Sector", "Message", "Applied"],
      ...items.map((p) => [p.name, p.email, p.phone, p.organization, p.sector || "", (p.message || "").replace(/\n/g, " "), p.created_at]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `eq-partners-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const allowed = ["admin", "district_leader"].includes(user?.role);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 fade-up">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">EQ Partners Campaign™</div>
          <h1 className="font-serif text-4xl text-[#0B1E36]">Partner applications</h1>
          <p className="text-sm text-slate-600 mt-2">{items.length} total · {filtered.length} shown</p>
        </div>
        {items.length > 0 && (
          <button onClick={exportCsv} data-testid="partners-export"
            className="inline-flex items-center gap-2 bg-[#0B1E36] text-white px-5 py-2.5 rounded-full font-semibold btn-lift hover:bg-[#132a4a]">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        )}
      </div>

      {!allowed ? (
        <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center">
          <div className="font-serif text-xl text-[#0B1E36] mb-2">Admin access required.</div>
          <p className="text-sm text-slate-600">Only Platform Admins and District Leaders can view partner applications.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center">
          <div className="font-serif text-xl text-[#0B1E36] mb-2">No applications yet.</div>
          <p className="text-sm text-slate-600">Share <span className="font-mono text-[#EC4899]">/partners</span> with your network to start collecting applications.</p>
        </div>
      ) : (
        <>
          <div className="mb-6 relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="partners-search"
              placeholder="Search by name, org, or sector…"
              className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
          </div>

          <div className="space-y-3">
            {filtered.map((p) => (
              <div key={p.id} className="bg-white p-6 rounded-2xl border border-slate-200 grid md:grid-cols-12 gap-4 items-start" data-testid={`partner-row-${p.id}`}>
                <div className="md:col-span-3">
                  <div className="font-serif text-lg text-[#0B1E36] leading-tight">{p.name}</div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Building2 className="h-3 w-3" /> {p.organization}</div>
                  {p.sector && <div className="text-[10px] uppercase tracking-widest text-[#C9A961] mt-2 font-semibold">{p.sector}</div>}
                </div>
                <div className="md:col-span-3 text-sm space-y-1">
                  <a href={`mailto:${p.email}`} className="flex items-center gap-2 text-[#EC4899] hover:underline"><Mail className="h-3.5 w-3.5" /> {p.email}</a>
                  <a href={`tel:${p.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-[#0B1E36]"><Phone className="h-3.5 w-3.5" /> {p.phone}</a>
                </div>
                <div className="md:col-span-4">
                  {p.message ? (
                    <p className="text-sm text-slate-700 italic leading-relaxed">"{p.message}"</p>
                  ) : (
                    <p className="text-xs text-slate-400">No message provided.</p>
                  )}
                </div>
                <div className="md:col-span-2 text-right">
                  <div className="text-xs text-slate-500 flex items-center gap-1 justify-end">
                    <Calendar className="h-3 w-3" /> {new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                  <div className="mt-2 inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-[#EC4899]/10 text-[#EC4899]">{p.status || "new"}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
